import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { search, selectLoading, selectResult } from "./SearchSlice";
import { CSSTransition } from "react-transition-group";

import { useTranslation } from "react-i18next";
import styled from "./Search.module.css";
import LoadingIcon from "@/features/common/loading";

import React from "react";
import {
  DeleteSearchHistory,
  SearchData,
  SearchHistory,
  SearchType,
  type IHistoryItem,
} from "./SearchAPI";
import { useNavigate } from "react-router-dom";
import { selectBlockChain } from "../address/addressSlice";
import { getIcon } from "@/utils/index"

export function Search() {
  const { t } = useTranslation();

  const loading = useAppSelector(selectLoading);
  const results = useAppSelector(selectResult);
  const blockchains = useAppSelector(selectBlockChain);
  const dispatch = useAppDispatch();
  const [keyword, setKeyword] = useState("");
  const [focus, setFocus] = useState(false);
  const [searchHistory, setSearchHistory] = useState<IHistoryItem[]>([]);

  const nodeRef = React.useRef(null);

  const navigate = useNavigate();

  const fetchHistory = async () => {
    const list = await SearchHistory({ limit: 10 });
    setSearchHistory(list);
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const doSearch = (value: string) => {
    const text = value.trim();
    if (!text) return;
    dispatch(search({ keyword: text }));
    setFocus(true);
  };

  const removeHistory = async (item: IHistoryItem) => {
    try {
      await DeleteSearchHistory(item.id);
      const latest = await SearchHistory({ limit: 10 });
      setSearchHistory(latest);
      if (latest.length === 0) setFocus(false);
    } catch (e) {
      console.log(e);
    }
  };

  const clickItem = (data: SearchData) => {
    switch (data.type) {
      case SearchType.Address:
        return navigate(`/address/1/${data.blockchain}/${data.name}`);
      case SearchType.Transaction:
        return navigate(`/trading/${data.blockchain}/${data.name}`);
      case SearchType.Entities:
        return navigate(`/entities/${data.name}/${data.id}`);
    }
  };

  const filteredHistory = searchHistory.filter((item) =>
    item.objectId.toLowerCase().includes(keyword.trim().toLowerCase()),
  );

  return (
    <div className={styled["search-container"]}>
      <div className={styled.inputwrap}>
        <input
          className={styled.input}
          value={keyword}
          placeholder={t("home.placeholder")}
          onChange={(e) => {
            const value = e.target.value;
            setKeyword(value);
            if (value.length > 0) {
              dispatch(search({ keyword: value }));
              setFocus(true);
            } else {
              setFocus(searchHistory.length > 0);
            }
          }}
          onBlur={() => setFocus(false)}
          onFocus={() => {
            if (keyword.length === 0) {
              fetchHistory();
            }
            setFocus(keyword.length > 0 || searchHistory.length > 0);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              doSearch(keyword);
            }
          }}
        />

        <button
          className={styled.button}
          onClick={() => {
            doSearch(keyword);
          }}
        >
          {t("home.button")}
        </button>
      </div>

      <CSSTransition
        in={focus}
        timeout={300}
        classNames="alert"
        nodeRef={nodeRef}
        unmountOnExit
      >
        <div ref={nodeRef} className={styled["select-container"]}>
          {loading ? (
            <div className={styled["loading-icon"]}>
              <LoadingIcon size={30} color={"#425466"} />
            </div>
          ) : keyword.trim().length === 0 ? (
            <div className={styled["history-wrap"]}>
              {searchHistory.length > 0 ? (
                searchHistory.map((item, idx) => (
                  <div
                    key={`history-key-${idx}`}
                    className={styled["history-row"]}
                  >
                    <button
                      className={styled["history-text"]}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setKeyword(item.objectId);
                        doSearch(item.objectId);
                      }}
                    >
                      {item.objectId}
                    </button>
                    <button
                      className={styled["history-delete"]}
                      aria-label="删除历史记录"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        removeHistory(item);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <p className={styled["empty-text"]}>暂无搜索历史</p>
              )}
            </div>
          ) : (
            <>
              <p className={styled["result-text"]}>
                {t("home.resultCountBefore")}{" "}
                <span>
                  {results.length}
                  {t("home.resultCountUnit")}
                </span>{" "}
                {t("home.resultCountAfter")}
              </p>
              <div className={styled["select-list"]}>
                {results.map((i, k) => (
                  <div key={`list-key-${k}`} className={styled["select-item"]}>
                    <div onClick={() => clickItem(i)}>
                      {i.type === SearchType.Entities ? null : (
                        <img
                          src={getIcon(i.blockchain)}
                          alt=""
                        />
                      )}
                      <p>{i.name}</p>
                    </div>
                  </div>
                ))}
              </div>

              {filteredHistory.length > 0 ? (
                <div className={styled["history-wrap"]}>
                  <p className={styled["result-text"]}>匹配的历史记录</p>
                  {filteredHistory.map((item, idx) => (
                    <div
                      key={`matched-history-key-${idx}`}
                      className={styled["history-row"]}
                    >
                      <button
                        className={styled["history-text"]}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setKeyword(item.objectId);
                          doSearch(item.objectId);
                        }}
                      >
                        {item.objectId}
                      </button>
                      <button
                        className={styled["history-delete"]}
                        aria-label="删除历史记录"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          removeHistory(item);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      </CSSTransition>
    </div>
  );
}
